import React, { useState, useEffect } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonMenuButton,
  IonLoading,
} from "@ionic/react";
import { useHistory } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  Autocomplete,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from "@mui/material";
import { Close, Add } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import { useForm, Controller } from "react-hook-form";
// import {
//   Ledger,
//   PackagePosting as PackagePostingType,
//   PackagePostingAccount,
//   PackagePostingReq,
// } from "../../types/ledger.types";
// import { useLedger } from "../../hooks/useLedger";
// import { useToast } from "../../contexts/ToastContext";
// import { ledgerPostingReferences } from "../../utils/consts";
import { formatCurrencyN } from "../../../../common/utils_t";
import { ledgerPostingReferences } from "../../../../common/consts";
import { useToast } from "../../../../context/ToastContext";
import { useLedger } from "../../../../hooks/useLedger";
import { Ledger,PackagePosting as PackagePostingType, PackagePostingAccount, PackagePostingReq } from "../../../../models/ledger.types";

const PackagePosting: React.FC = () => {
  const history = useHistory();
  const { ledgers, loading, fetchLedgers, postPackageAccount } = useLedger();
  const { showError } = useToast();
  const [transactionDate] = useState<Dayjs>(dayjs());
  const [mainOperation, setMainOperation] = useState<"dr" | "cr">("dr");
  const [mainAccount, setMainAccount] = useState<Ledger | null>(null);
  const [selectedLedger, setSelectedLedger] = useState<Ledger | null>(null);
  const [ledgerAmount, setLedgerAmount] = useState<number>(0);
  const [addedAccounts, setAddedAccounts] = useState<PackagePostingAccount[]>(
    []
  );
  const [mainAccountSearchInput, setMainAccountSearchInput] = useState("");
  const [ledgerSearchInput, setLedgerSearchInput] = useState("");

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<PackagePostingType>({
    defaultValues: {
      desp: "",
      reference: "",
      referenceDetail: "",
    },
  });

  useEffect(() => {
    fetchLedgers();
  }, [fetchLedgers]);

  const handleAddAccount = () => {
    if (!selectedLedger || !selectedLedger.sn) {
      showError("Please select a ledger");
      return;
    }

    if (ledgerAmount <= 0) {
      showError("Amount must be greater than 0");
      return;
    }

    // Check if ledger is already added
    if (addedAccounts.some((acc) => acc.sn === selectedLedger.sn)) {
      showError("This ledger has already been added");
      return;
    }

    const oppositeOperation = mainOperation === "dr" ? "cr" : "dr";
    const newAccount: PackagePostingAccount = {
      sn: selectedLedger.sn,
      amount: ledgerAmount,
      transType: oppositeOperation,
    };

    setAddedAccounts([...addedAccounts, newAccount]);
    setSelectedLedger(null);
    setLedgerAmount(0);
    setLedgerSearchInput("");
  };

  const handleRemoveAccount = (sn: string) => {
    setAddedAccounts(addedAccounts.filter((acc) => acc.sn !== sn));
  };

  const calculateTotal = () => {
    return addedAccounts.reduce((sum, acc) => sum + acc.amount, 0);
  };

  const onSubmit = async (data: PackagePostingType) => {
    if (!mainAccount || !mainAccount.sn) {
      showError("Please select the main account");
      return;
    }

    if (addedAccounts.length === 0) {
      showError("Please add at least one account");
      return;
    }

    const total = calculateTotal();
    if (total <= 0) {
      showError("Total amount must be greater than 0");
      return;
    }

    const posting: PackagePostingReq = {
      accounts: [
        ...addedAccounts,
        {
          sn: mainAccount.sn,
          amount: total,
          transType: mainOperation,
        },
      ],
      desp: data.desp,
      reference: data.reference || "",
      referenceDetail: data.referenceDetail || "",
    };

    const success = await postPackageAccount(posting);
    if (success) {
      history.push("/core-accounting/general-ledger");
    }
  };

  // Filter out already added ledgers and main account from available options
  const availableLedgers = ledgers.filter(
    (ledger) =>
      ledger.sn !== mainAccount?.sn &&
      !addedAccounts.some((acc) => acc.sn === ledger.sn)
  );

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Package Posting</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => history.goBack()}>Cancel</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <Box sx={{ p: 2 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Posting Form - ({transactionDate.format("DD/MM/YYYY")})
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                {/* Operation Type Selection */}
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Operation Type
                  </Typography>
                  <ToggleButtonGroup
                    value={mainOperation}
                    exclusive
                    onChange={(_, newValue) => {
                      if (newValue !== null) {
                        setMainOperation(newValue);
                        setMainAccount(null);
                        setAddedAccounts([]);
                        setMainAccountSearchInput("");
                      }
                    }}
                    aria-label="operation type"
                    fullWidth
                    sx={{
                      "& .MuiToggleButton-root": {
                        textTransform: "none",
                        py: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        "&.Mui-selected": {
                          backgroundColor: "primary.main",
                          color: "primary.contrastText",
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                        },
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      },
                    }}
                  >
                    <ToggleButton
                      value="dr"
                      aria-label="debit to multiple credit"
                    >
                      Debit one, Credit multiple
                    </ToggleButton>
                    <ToggleButton
                      value="cr"
                      aria-label="credit to multiple debit"
                    >
                      Credit one, Debit multiple
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                <Divider sx={{ width: "100%", my: 2 }} />

                {/* Main Account Selection */}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={ledgers}
                    getOptionLabel={(option) =>
                      option.name || option.account_code || ""
                    }
                    value={mainAccount}
                    onChange={(_, newValue) => {
                      setMainAccount(newValue);
                      setMainAccountSearchInput("");
                    }}
                    inputValue={mainAccountSearchInput}
                    onInputChange={(_, newInputValue) => {
                      setMainAccountSearchInput(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`Select ${
                          mainOperation === "dr" ? "Debit" : "Credit"
                        } Account*`}
                        required
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        {...props}
                        key={option.id || option.sn}
                      >
                        <Box>
                          <Typography variant="body1">{option.name}</Typography>
                          {option.account_code && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Code: {option.account_code}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  />
                </Grid>

                {/* Total Amount Display */}
                {mainAccount && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ pt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Total {mainOperation === "dr" ? "Credit" : "Debit"}{" "}
                        Amount
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {formatCurrencyN(calculateTotal())}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                <Divider sx={{ width: "100%", my: 2 }} />

                {/* Add Multiple Accounts Section */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Add {mainOperation === "dr" ? "Credit" : "Debit"} Accounts
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={5}>
                      <Autocomplete
                        options={availableLedgers}
                        getOptionLabel={(option) =>
                          option.name || option.account_code || ""
                        }
                        value={selectedLedger}
                        onChange={(_, newValue) => {
                          setSelectedLedger(newValue);
                          setLedgerSearchInput("");
                        }}
                        inputValue={ledgerSearchInput}
                        onInputChange={(_, newInputValue) => {
                          setLedgerSearchInput(newInputValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={`Select ${
                              mainOperation === "dr" ? "Credit" : "Debit"
                            } Ledger`}
                            fullWidth
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box
                            component="li"
                            {...props}
                            key={option.id || option.sn}
                          >
                            <Box>
                              <Typography variant="body1">
                                {option.name}
                              </Typography>
                              {option.account_code && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Code: {option.account_code}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        type="number"
                        label="Amount"
                        fullWidth
                        value={ledgerAmount || ""}
                        onChange={(e) =>
                          setLedgerAmount(parseFloat(e.target.value) || 0)
                        }
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ mr: 1 }}>₦</Typography>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<Add />}
                        onClick={handleAddAccount}
                        disabled={!selectedLedger || ledgerAmount <= 0}
                        sx={{ height: "56px" }}
                      >
                        Add Account
                      </Button>
                    </Grid>
                  </Grid>

                  {/* Display Added Accounts as Chips */}
                  {addedAccounts.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Added Accounts:
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {addedAccounts.map((acc) => (
                          <Chip
                            key={acc.sn}
                            label={`${acc.sn} - ${formatCurrencyN(acc.amount)}`}
                            onDelete={() => handleRemoveAccount(acc.sn)}
                            deleteIcon={<Close />}
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Total: {formatCurrencyN(calculateTotal())}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Grid>

                <Divider sx={{ width: "100%", my: 2 }} />

                {/* Transaction Description */}
                <Grid item xs={12}>
                  <Controller
                    name="desp"
                    control={control}
                    rules={{ required: "Transaction description is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Transaction Description"
                        fullWidth
                        required
                        error={!!errors.desp}
                        helperText={errors.desp?.message}
                      />
                    )}
                  />
                </Grid>

                {/* References */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="reference"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="References"
                        fullWidth
                        select
                        SelectProps={{
                          native: true,
                        }}
                      >
                        <option value="">Select Reference</option>
                        {ledgerPostingReferences.map((ref) => (
                          <option key={ref.value} value={ref.value}>
                            {ref.label}
                          </option>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                {/* Reference Details */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="referenceDetail"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Reference Details"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
                  >
                    <Button variant="outlined" onClick={() => history.goBack()}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={
                        loading || !mainAccount || addedAccounts.length === 0
                      }
                    >
                      POST TRANSACTION
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>
      </IonContent>
      <IonLoading isOpen={loading} message="Processing..." />
    </IonPage>
  );
};

export default PackagePosting;
